import * as React from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { ParishProvider, useParish } from "@/contexts/parish-context";
import SelectParish from "@/pages/select-parish";
import { I18nProvider } from "@/lib/i18n/i18n";
import { FloatingPagesMenu } from "@/components/floating-pages-menu";
import { ParishStatusBanner } from "@/components/ParishStatusBanner";
import { AppSidebar } from "@/components/app-sidebar";
import { MobileNavTrigger } from "@/components/MobileNavTrigger";
import { AppShellHeader } from "@/components/layout/app-shell";
import { AideEnTete } from "@/components/AideEnTete";
import { canAccessPage } from "@/lib/role-permissions";
import { AccesRestreint } from "@/components/acces-restreint";
import NotFound from "@/pages/not-found";
import AuthPage from "@/pages/auth-page";
import ChoisirMotDePasse from "@/pages/choisir-mot-de-passe";
import PagePlaceholder from "@/pages/page-placeholder";
import AdminGuard from "@/components/admin-guard";
import { COMING_SOON_TITLE_KEYS } from "@/lib/menu-sections";
import { useDefilementEcran } from "@/hooks/useDefilementEcran";

const Dashboard = React.lazy(() => import("@/pages/dashboard"));
const Annuaire = React.lazy(() => import("@/pages/annuaire"));
const Profil = React.lazy(() => import("@/pages/profil"));
const Segmentation = React.lazy(() => import("@/pages/segmentation"));
const Registres = React.lazy(() => import("@/pages/registres"));
const Intentions = React.lazy(() => import("@/pages/intentions"));
const Finances = React.lazy(() => import("@/pages/finances"));
const Cloture = React.lazy(() => import("@/pages/cloture"));
const Communication = React.lazy(() => import("@/pages/communication"));
const Siteweb = React.lazy(() => import("@/pages/siteweb"));
const AssistantIA = React.lazy(() => import("@/pages/assistant-ia"));
const Analytics = React.lazy(() => import("@/pages/analytics"));
const Conseil = React.lazy(() => import("@/pages/conseil"));
const RH = React.lazy(() => import("@/pages/rh"));
const Urgences = React.lazy(() => import("@/pages/urgences"));
const Formation = React.lazy(() => import("@/pages/formation"));
const Ecommerce = React.lazy(() => import("@/pages/ecommerce"));
const Catalogue = React.lazy(() => import("@/pages/catalogue"));
const Logistique = React.lazy(() => import("@/pages/logistique"));
const Calendrier = React.lazy(() => import("@/pages/calendrier"));
const Evenements = React.lazy(() => import("@/pages/evenements"));
const Reservations = React.lazy(() => import("@/pages/reservations"));
const Batiment = React.lazy(() => import("@/pages/batiment"));
const Inventaire = React.lazy(() => import("@/pages/inventaire"));
const Vehicules = React.lazy(() => import("@/pages/vehicules"));
const Funerailles = React.lazy(() => import("@/pages/funerailles"));
const Cimetiere = React.lazy(() => import("@/pages/cimetiere"));
const Securite = React.lazy(() => import("@/pages/securite"));
const Crises = React.lazy(() => import("@/pages/crises"));
const Jardins = React.lazy(() => import("@/pages/jardins"));
const Archives = React.lazy(() => import("@/pages/archives"));
const Juridique = React.lazy(() => import("@/pages/juridique"));
const Reporting = React.lazy(() => import("@/pages/reporting"));
const Catechese = React.lazy(() => import("@/pages/catechese"));
const PreparationSacrements = React.lazy(() => import("@/pages/preparation-sacrements"));
const FormationAdultes = React.lazy(() => import("@/pages/formation-adultes"));
const Bibliotheque = React.lazy(() => import("@/pages/bibliotheque"));
const Mouvements = React.lazy(() => import("@/pages/mouvements"));
const EnseignementsMouvement = React.lazy(() => import("@/pages/enseignements-mouvement"));
const CoordinationDiocesaine = React.lazy(() => import("@/pages/coordination-diocesaine"));
const Media = React.lazy(() => import("@/pages/media"));
const MediaGuide = React.lazy(() => import("@/pages/media-guide"));
const Jumelages = React.lazy(() => import("@/pages/jumelages"));
const Missions = React.lazy(() => import("@/pages/missions"));
const AnalyticsPastoral = React.lazy(() => import("@/pages/analytics-pastoral"));
const Vocations = React.lazy(() => import("@/pages/vocations"));
const Supervision = React.lazy(() => import("@/pages/supervision"));
const Feedback = React.lazy(() => import("@/pages/feedback"));
const Capitalisation = React.lazy(() => import("@/pages/capitalisation"));
const Clerge = React.lazy(() => import("@/pages/clerge"));
const CPP = React.lazy(() => import("@/pages/cpp"));
const CPAE = React.lazy(() => import("@/pages/cpae"));
const Comptage = React.lazy(() => import("@/pages/comptage"));
const Commissions = React.lazy(() => import("@/pages/commissions"));
const AgendaCure = React.lazy(() => import("@/pages/agenda-cure"));
const Courrier = React.lazy(() => import("@/pages/courrier"));
const Donateurs = React.lazy(() => import("@/pages/donateurs"));
const Patrimoine = React.lazy(() => import("@/pages/patrimoine"));
const InventaireDetail = React.lazy(() => import("@/pages/inventaire-detail"));
const Stocks = React.lazy(() => import("@/pages/stocks"));
const DashboardEconome = React.lazy(() => import("@/pages/dashboard-econome"));
const DashboardCPAE = React.lazy(() => import("@/pages/dashboard-cpae"));
const Comptabilite = React.lazy(() => import("@/pages/comptabilite"));
const Parametres = React.lazy(() => import("@/pages/parametres"));
const HorairesMesses = React.lazy(() => import("@/pages/horaires-messes"));
const Collecte = React.lazy(() => import("@/pages/collecte"));
const Reversements = React.lazy(() => import("@/pages/reversements"));
const Encaissements = React.lazy(() => import("@/pages/encaissements"));
const MaSemaine = React.lazy(() => import("@/pages/ma-semaine"));
const DashboardVicaire = React.lazy(() => import("@/pages/dashboard-vicaire"));
const PlanningLiturgique = React.lazy(() => import("@/pages/planning-liturgique"));
const Mariage = React.lazy(() => import("@/pages/mariage"));
const MobileMoney = React.lazy(() => import("@/pages/mobile-money"));
const GroupesPriere = React.lazy(() => import("@/pages/groupes-priere"));
const VisitesMalades = React.lazy(() => import("@/pages/visites-malades"));
const AidesSociales = React.lazy(() => import("@/pages/aides-sociales"));
const Kermesse = React.lazy(() => import("@/pages/kermesse"));
const Notifications = React.lazy(() => import("@/pages/notifications"));
const PortailFidele = React.lazy(() => import("@/pages/portail-fidele"));
const RapportDiocese = React.lazy(() => import("@/pages/rapport-diocese"));
const Assistance = React.lazy(() => import("@/pages/assistance"));
const Signalements = React.lazy(() => import("@/pages/signalements"));
const ComingSoonPage = React.lazy(() => import("@/pages/a-venir"));
const AdminHub = React.lazy(() => import("@/pages/admin"));
const AdminModeration = React.lazy(() => import("@/pages/admin/moderation"));
const AdminDioceseActualites = React.lazy(() => import("@/pages/admin/diocese-actualites"));
const AdminDioceseCommuniques = React.lazy(() => import("@/pages/admin/diocese-communiques"));
const AdminParishHistoire = React.lazy(() => import("@/pages/admin/parish-histoire"));
const AdminParishPatrimoine = React.lazy(() => import("@/pages/admin/parish-patrimoine"));
const AdminParishAssociations = React.lazy(() => import("@/pages/admin/parish-associations"));
const AdminParishDelegations = React.lazy(() => import("@/pages/admin/parish-delegations"));

// Vitrines « à venir » (09/10/2026, manuel 2) : une page claire qui dit que
// la fonction n'est pas encore disponible, au lieu d'une coquille de « · ».
// Composants créés une fois (références stables pour le routeur).
const COMING_SOON_PAGES: Record<number, React.ComponentType> = Object.fromEntries(
  Object.entries(COMING_SOON_TITLE_KEYS).map(([id, titleKey]) => {
    const Page = () => <ComingSoonPage titleKey={titleKey} />;
    Page.displayName = `ComingSoon${id}`;
    return [Number(id), Page];
  }),
);

export function ProtectedRoute({ component: Component, pageId }: { component: React.ComponentType; pageId?: number }) {
  const { user, isLoading } = useAuth();
  const { currentParishId } = useParish();

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #1A2B4A 0%, #2a4a7f 40%, #3b7dd8 100%)" }}>
        <div className="flex flex-col items-center gap-4">
          <img src="/images/logo-white.png" alt="CATHO+" className="h-10 opacity-80" />
          <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <AuthPage />;
  }

  // Mot de passe provisoire de la fiche de connexion : la personne choisit le
  // sien avant toute autre page (27/10/2026).
  if (user.mustChangePassword) {
    return <ChoisirMotDePasse />;
  }

  // Multi-tenant V1.5 — si aucune paroisse active, rediriger vers /select-parish.
  // C'est le cas pour les admins globaux (edaci_admin) après login.
  if (!currentParishId) {
    return <SelectParish />;
  }

  if (pageId && !canAccessPage(user.role, pageId)) {
    return <AccesRestreint />;
  }

  return <Component />;
}


function RedirectWithQuery({ to }: { to: string }) {
  const [, setLocation] = useLocation();
  const search = window.location.search;
  React.useEffect(() => { setLocation(to + search); }, [to, search, setLocation]);
  return null;
}

/**
 * Attente du chargement d'un écran. Hors de l'espace connecté (connexion) :
 * l'écran d'accueil bleu. Dans l'espace : une simple roue DANS la zone de
 * contenu, le menu restant affiché ; l'écran bleu plein écran y faisait
 * croire à un rechargement de l'application (tournage du 10/10/2026).
 */
export function PageFallback() {
  const { user } = useAuth();
  const [location] = useLocation();
  const horsEspace = !user || location === "/" || location === "/concept";
  if (!horsEspace) {
    return (
      <div className="flex min-h-[60vh] w-full items-center justify-center" role="status" aria-label="Chargement de l'écran" data-testid="page-fallback-contenu">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-[var(--brand-navy)] dark:border-gray-700 dark:border-t-gray-200" />
      </div>
    );
  }
  return (
    <div className="flex h-screen w-screen items-center justify-center" style={{ background: "linear-gradient(135deg, #1A2B4A 0%, #2a4a7f 40%, #3b7dd8 100%)" }}>
      <div className="flex flex-col items-center gap-3">
        <img src="/images/logo-white.png" alt="CATHO+" className="h-10 opacity-80" />
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-white" />
      </div>
    </div>
  );
}

function Router() {
  return (
    <React.Suspense fallback={<PageFallback />}>
    <Switch>
      <Route path="/" component={AuthPage} />
      <Route path="/select-parish" component={SelectParish} />
      <Route path="/dashboard"><ProtectedRoute component={Dashboard} /></Route>
      <Route path="/annuaire"><ProtectedRoute component={Annuaire} pageId={3} /></Route>
      <Route path="/profil"><ProtectedRoute component={Profil} pageId={4} /></Route>
      <Route path="/segmentation"><ProtectedRoute component={Segmentation} pageId={5} /></Route>
      <Route path="/registres"><ProtectedRoute component={Registres} pageId={6} /></Route>
      <Route path="/intentions"><ProtectedRoute component={Intentions} pageId={7} /></Route>
      <Route path="/finances"><ProtectedRoute component={Finances} pageId={8} /></Route>
      <Route path="/cloture"><ProtectedRoute component={Cloture} pageId={9} /></Route>
      <Route path="/communication"><ProtectedRoute component={Communication} pageId={10} /></Route>
      <Route path="/siteweb"><ProtectedRoute component={Siteweb} pageId={11} /></Route>
      <Route path="/assistant-ia"><ProtectedRoute component={AssistantIA} pageId={12} /></Route>
      <Route path="/analytics"><ProtectedRoute component={Analytics} pageId={13} /></Route>
      <Route path="/conseil"><ProtectedRoute component={Conseil} pageId={14} /></Route>
      <Route path="/rh"><ProtectedRoute component={RH} pageId={15} /></Route>
      <Route path="/realite-augmentee"><ProtectedRoute component={COMING_SOON_PAGES[16]} pageId={16} /></Route>
      <Route path="/domotique"><ProtectedRoute component={COMING_SOON_PAGES[17]} pageId={17} /></Route>
      <Route path="/urgences"><ProtectedRoute component={Urgences} pageId={18} /></Route>
      <Route path="/innovation"><ProtectedRoute component={COMING_SOON_PAGES[19]} pageId={19} /></Route>
      <Route path="/formation"><ProtectedRoute component={Formation} pageId={20} /></Route>
      <Route path="/ecommerce"><ProtectedRoute component={Ecommerce} pageId={21} /></Route>
      <Route path="/catalogue"><ProtectedRoute component={Catalogue} pageId={22} /></Route>
      <Route path="/logistique"><ProtectedRoute component={Logistique} pageId={23} /></Route>
      <Route path="/calendrier"><ProtectedRoute component={Calendrier} pageId={24} /></Route>
      <Route path="/evenements"><ProtectedRoute component={Evenements} pageId={25} /></Route>
      <Route path="/reservations"><ProtectedRoute component={Reservations} pageId={26} /></Route>
      <Route path="/batiment"><ProtectedRoute component={Batiment} pageId={27} /></Route>
      <Route path="/inventaire"><ProtectedRoute component={Inventaire} pageId={28} /></Route>
      <Route path="/vehicules"><ProtectedRoute component={Vehicules} pageId={29} /></Route>
      <Route path="/funerailles"><ProtectedRoute component={Funerailles} pageId={30} /></Route>
      <Route path="/cimetiere"><ProtectedRoute component={Cimetiere} pageId={31} /></Route>
      <Route path="/securite"><ProtectedRoute component={Securite} pageId={32} /></Route>
      <Route path="/crises"><ProtectedRoute component={Crises} pageId={33} /></Route>
      <Route path="/energie"><ProtectedRoute component={COMING_SOON_PAGES[34]} pageId={34} /></Route>
      <Route path="/jardins"><ProtectedRoute component={Jardins} pageId={35} /></Route>
      <Route path="/archives"><ProtectedRoute component={Archives} pageId={36} /></Route>
      <Route path="/administration"><ProtectedRoute component={COMING_SOON_PAGES[37]} pageId={37} /></Route>
      <Route path="/impressions"><ProtectedRoute component={COMING_SOON_PAGES[38]} pageId={38} /></Route>
      <Route path="/juridique"><ProtectedRoute component={Juridique} pageId={39} /></Route>
      <Route path="/reporting"><ProtectedRoute component={Reporting} pageId={40} /></Route>
      <Route path="/catechese"><ProtectedRoute component={Catechese} pageId={41} /></Route>
      <Route path="/preparation-sacrements"><ProtectedRoute component={PreparationSacrements} pageId={42} /></Route>
      <Route path="/formation-adultes"><ProtectedRoute component={FormationAdultes} pageId={43} /></Route>
      <Route path="/bibliotheque"><ProtectedRoute component={Bibliotheque} pageId={44} /></Route>
      <Route path="/mouvements/:groupeId/enseignements"><ProtectedRoute component={EnseignementsMouvement} pageId={45} /></Route>
      <Route path="/mouvements"><ProtectedRoute component={Mouvements} pageId={45} /></Route>
      <Route path="/coordination-diocesaine"><ProtectedRoute component={CoordinationDiocesaine} pageId={46} /></Route>
      <Route path="/media/guide"><ProtectedRoute component={MediaGuide} pageId={89} /></Route>
      <Route path="/media"><ProtectedRoute component={Media} pageId={47} /></Route>
      <Route path="/reseaux-sociaux"><ProtectedRoute component={COMING_SOON_PAGES[48]} pageId={48} /></Route>
      <Route path="/siteweb-avance"><ProtectedRoute component={COMING_SOON_PAGES[49]} pageId={49} /></Route>
      <Route path="/jumelages"><ProtectedRoute component={Jumelages} pageId={50} /></Route>
      <Route path="/missions"><ProtectedRoute component={Missions} pageId={51} /></Route>
      <Route path="/analytics-pastoral"><ProtectedRoute component={AnalyticsPastoral} pageId={52} /></Route>
      <Route path="/vocations"><ProtectedRoute component={Vocations} pageId={53} /></Route>
      <Route path="/labo-innovation"><ProtectedRoute component={COMING_SOON_PAGES[54]} pageId={54} /></Route>
      <Route path="/recherche-theologique"><ProtectedRoute component={COMING_SOON_PAGES[55]} pageId={55} /></Route>
      <Route path="/universite-clerge"><ProtectedRoute component={COMING_SOON_PAGES[56]} pageId={56} /></Route>
      <Route path="/supervision"><ProtectedRoute component={Supervision} pageId={57} /></Route>
      <Route path="/qualite"><ProtectedRoute component={COMING_SOON_PAGES[58]} pageId={58} /></Route>
      <Route path="/feedback"><ProtectedRoute component={Feedback} pageId={59} /></Route>
      <Route path="/capitalisation"><ProtectedRoute component={Capitalisation} pageId={60} /></Route>
      <Route path="/clerge"><ProtectedRoute component={Clerge} pageId={61} /></Route>
      <Route path="/cpp"><ProtectedRoute component={CPP} pageId={62} /></Route>
      <Route path="/cpae"><ProtectedRoute component={CPAE} pageId={63} /></Route>
      <Route path="/comptage"><ProtectedRoute component={Comptage} pageId={64} /></Route>
      <Route path="/commissions"><ProtectedRoute component={Commissions} pageId={65} /></Route>
      <Route path="/agenda-cure"><ProtectedRoute component={AgendaCure} pageId={66} /></Route>
      <Route path="/courrier"><ProtectedRoute component={Courrier} pageId={67} /></Route>
      <Route path="/donateurs"><ProtectedRoute component={Donateurs} pageId={68} /></Route>
      <Route path="/patrimoine"><ProtectedRoute component={Patrimoine} pageId={69} /></Route>
      <Route path="/inventaire-detail"><ProtectedRoute component={InventaireDetail} pageId={70} /></Route>
      <Route path="/stocks"><ProtectedRoute component={Stocks} pageId={71} /></Route>
      <Route path="/dashboard-cure"><RedirectWithQuery to="/dashboard" /></Route>
      <Route path="/dashboard-econome"><ProtectedRoute component={DashboardEconome} pageId={73} /></Route>
      <Route path="/dashboard-cpae"><ProtectedRoute component={DashboardCPAE} pageId={74} /></Route>
      <Route path="/comptabilite"><ProtectedRoute component={Comptabilite} pageId={75} /></Route>
      <Route path="/parametres"><ProtectedRoute component={Parametres} pageId={76} /></Route>
      <Route path="/horaires-messes"><ProtectedRoute component={HorairesMesses} pageId={92} /></Route>
      <Route path="/collecte"><ProtectedRoute component={Collecte} pageId={77} /></Route>
      {/* C2 (19/10/2026) : la secrétaire encaisse au guichet. */}
      <Route path="/encaissements"><ProtectedRoute component={Encaissements} pageId={91} /></Route>
      <Route path="/ma-semaine"><ProtectedRoute component={MaSemaine} pageId={93} /></Route>
      <Route path="/reversements"><ProtectedRoute component={Reversements} pageId={90} /></Route>
      <Route path="/dashboard-vicaire"><ProtectedRoute component={DashboardVicaire} pageId={78} /></Route>
      <Route path="/planning-liturgique"><ProtectedRoute component={PlanningLiturgique} pageId={79} /></Route>
      <Route path="/mariage"><ProtectedRoute component={Mariage} pageId={80} /></Route>
      <Route path="/mobile-money"><ProtectedRoute component={MobileMoney} pageId={81} /></Route>
      <Route path="/groupes-priere"><ProtectedRoute component={GroupesPriere} pageId={82} /></Route>
      <Route path="/visites-malades"><ProtectedRoute component={VisitesMalades} pageId={83} /></Route>
      <Route path="/aides-sociales"><ProtectedRoute component={AidesSociales} pageId={84} /></Route>
      <Route path="/kermesse"><ProtectedRoute component={Kermesse} pageId={85} /></Route>
      <Route path="/notifications"><ProtectedRoute component={Notifications} pageId={86} /></Route>
      <Route path="/portail-fidele"><ProtectedRoute component={PortailFidele} pageId={87} /></Route>
      <Route path="/rapport-diocese"><ProtectedRoute component={RapportDiocese} pageId={88} /></Route>
      {/* Assistance (manuel 2, Action 06) : ouverte à tous les rôles. */}
      <Route path="/assistance"><ProtectedRoute component={Assistance} /></Route>
      <Route path="/assistance/signalements"><ProtectedRoute component={Signalements} /></Route>
      {/* Espace admin (Lot Web-2) — wrappé dans AdminGuard pour filtrage rôle */}
      <Route path="/admin"><ProtectedRoute component={() => <AdminGuard><AdminHub /></AdminGuard>} /></Route>
      <Route path="/admin/moderation"><ProtectedRoute component={() => <AdminGuard><AdminModeration /></AdminGuard>} /></Route>
      <Route path="/admin/diocese-actualites"><ProtectedRoute component={() => <AdminGuard><AdminDioceseActualites /></AdminGuard>} /></Route>
      <Route path="/admin/diocese-communiques"><ProtectedRoute component={() => <AdminGuard><AdminDioceseCommuniques /></AdminGuard>} /></Route>
      <Route path="/admin/parish-histoire"><ProtectedRoute component={() => <AdminGuard><AdminParishHistoire /></AdminGuard>} /></Route>
      <Route path="/admin/parish-patrimoine"><ProtectedRoute component={() => <AdminGuard><AdminParishPatrimoine /></AdminGuard>} /></Route>
      <Route path="/admin/parish-associations"><ProtectedRoute component={() => <AdminGuard><AdminParishAssociations /></AdminGuard>} /></Route>
      <Route path="/admin/parish-delegations"><ProtectedRoute component={() => <AdminGuard allowedRoles={["admin","super_admin","edaci_admin","cure"]}><AdminParishDelegations /></AdminGuard>} /></Route>


      {/* Legacy /page/N redirects (Web-routes-semantiques refactor) */}
      <Route path="/dashboard"><RedirectWithQuery to="/dashboard" /></Route>
      <Route path="/annuaire"><RedirectWithQuery to="/annuaire" /></Route>
      <Route path="/profil"><RedirectWithQuery to="/profil" /></Route>
      <Route path="/segmentation"><RedirectWithQuery to="/segmentation" /></Route>
      <Route path="/registres"><RedirectWithQuery to="/registres" /></Route>
      <Route path="/intentions"><RedirectWithQuery to="/intentions" /></Route>
      <Route path="/finances"><RedirectWithQuery to="/finances" /></Route>
      <Route path="/cloture"><RedirectWithQuery to="/cloture" /></Route>
      <Route path="/communication"><RedirectWithQuery to="/communication" /></Route>
      <Route path="/siteweb"><RedirectWithQuery to="/siteweb" /></Route>
      <Route path="/assistant-ia"><RedirectWithQuery to="/assistant-ia" /></Route>
      <Route path="/analytics"><RedirectWithQuery to="/analytics" /></Route>
      <Route path="/conseil"><RedirectWithQuery to="/conseil" /></Route>
      <Route path="/rh"><RedirectWithQuery to="/rh" /></Route>
      <Route path="/realite-augmentee"><RedirectWithQuery to="/realite-augmentee" /></Route>
      <Route path="/domotique"><RedirectWithQuery to="/domotique" /></Route>
      <Route path="/urgences"><RedirectWithQuery to="/urgences" /></Route>
      <Route path="/innovation"><RedirectWithQuery to="/innovation" /></Route>
      <Route path="/formation"><RedirectWithQuery to="/formation" /></Route>
      <Route path="/ecommerce"><RedirectWithQuery to="/ecommerce" /></Route>
      <Route path="/catalogue"><RedirectWithQuery to="/catalogue" /></Route>
      <Route path="/logistique"><RedirectWithQuery to="/logistique" /></Route>
      <Route path="/calendrier"><RedirectWithQuery to="/calendrier" /></Route>
      <Route path="/evenements"><RedirectWithQuery to="/evenements" /></Route>
      <Route path="/reservations"><RedirectWithQuery to="/reservations" /></Route>
      <Route path="/batiment"><RedirectWithQuery to="/batiment" /></Route>
      <Route path="/inventaire"><RedirectWithQuery to="/inventaire" /></Route>
      <Route path="/vehicules"><RedirectWithQuery to="/vehicules" /></Route>
      <Route path="/funerailles"><RedirectWithQuery to="/funerailles" /></Route>
      <Route path="/cimetiere"><RedirectWithQuery to="/cimetiere" /></Route>
      <Route path="/securite"><RedirectWithQuery to="/securite" /></Route>
      <Route path="/crises"><RedirectWithQuery to="/crises" /></Route>
      <Route path="/energie"><RedirectWithQuery to="/energie" /></Route>
      <Route path="/jardins"><RedirectWithQuery to="/jardins" /></Route>
      <Route path="/archives"><RedirectWithQuery to="/archives" /></Route>
      <Route path="/administration"><RedirectWithQuery to="/administration" /></Route>
      <Route path="/impressions"><RedirectWithQuery to="/impressions" /></Route>
      <Route path="/juridique"><RedirectWithQuery to="/juridique" /></Route>
      <Route path="/reporting"><RedirectWithQuery to="/reporting" /></Route>
      <Route path="/catechese"><RedirectWithQuery to="/catechese" /></Route>
      <Route path="/preparation-sacrements"><RedirectWithQuery to="/preparation-sacrements" /></Route>
      <Route path="/formation-adultes"><RedirectWithQuery to="/formation-adultes" /></Route>
      <Route path="/bibliotheque"><RedirectWithQuery to="/bibliotheque" /></Route>
      <Route path="/mouvements"><RedirectWithQuery to="/mouvements" /></Route>
      <Route path="/coordination-diocesaine"><RedirectWithQuery to="/coordination-diocesaine" /></Route>
      <Route path="/media"><RedirectWithQuery to="/media" /></Route>
      <Route path="/reseaux-sociaux"><RedirectWithQuery to="/reseaux-sociaux" /></Route>
      <Route path="/siteweb-avance"><RedirectWithQuery to="/siteweb-avance" /></Route>
      <Route path="/jumelages"><RedirectWithQuery to="/jumelages" /></Route>
      <Route path="/missions"><RedirectWithQuery to="/missions" /></Route>
      <Route path="/analytics-pastoral"><RedirectWithQuery to="/analytics-pastoral" /></Route>
      <Route path="/vocations"><RedirectWithQuery to="/vocations" /></Route>
      <Route path="/labo-innovation"><RedirectWithQuery to="/labo-innovation" /></Route>
      <Route path="/recherche-theologique"><RedirectWithQuery to="/recherche-theologique" /></Route>
      <Route path="/universite-clerge"><RedirectWithQuery to="/universite-clerge" /></Route>
      <Route path="/supervision"><RedirectWithQuery to="/supervision" /></Route>
      <Route path="/qualite"><RedirectWithQuery to="/qualite" /></Route>
      <Route path="/feedback"><RedirectWithQuery to="/feedback" /></Route>
      <Route path="/capitalisation"><RedirectWithQuery to="/capitalisation" /></Route>
      <Route path="/clerge"><RedirectWithQuery to="/clerge" /></Route>
      <Route path="/cpp"><RedirectWithQuery to="/cpp" /></Route>
      <Route path="/cpae"><RedirectWithQuery to="/cpae" /></Route>
      <Route path="/comptage"><RedirectWithQuery to="/comptage" /></Route>
      <Route path="/commissions"><RedirectWithQuery to="/commissions" /></Route>
      <Route path="/agenda-cure"><RedirectWithQuery to="/agenda-cure" /></Route>
      <Route path="/courrier"><RedirectWithQuery to="/courrier" /></Route>
      <Route path="/donateurs"><RedirectWithQuery to="/donateurs" /></Route>
      <Route path="/patrimoine"><RedirectWithQuery to="/patrimoine" /></Route>
      <Route path="/inventaire-detail"><RedirectWithQuery to="/inventaire-detail" /></Route>
      <Route path="/stocks"><RedirectWithQuery to="/stocks" /></Route>
      <Route path="/dashboard-cure"><RedirectWithQuery to="/dashboard" /></Route>
      <Route path="/dashboard-econome"><RedirectWithQuery to="/dashboard-econome" /></Route>
      <Route path="/dashboard-cpae"><RedirectWithQuery to="/dashboard-cpae" /></Route>
      <Route path="/comptabilite"><RedirectWithQuery to="/comptabilite" /></Route>
      <Route path="/parametres"><RedirectWithQuery to="/parametres" /></Route>
      <Route path="/collecte"><RedirectWithQuery to="/collecte" /></Route>
      <Route path="/dashboard-vicaire"><RedirectWithQuery to="/dashboard-vicaire" /></Route>
      <Route path="/planning-liturgique"><RedirectWithQuery to="/planning-liturgique" /></Route>
      <Route path="/mariage"><RedirectWithQuery to="/mariage" /></Route>
      <Route path="/mobile-money"><RedirectWithQuery to="/mobile-money" /></Route>
      <Route path="/groupes-priere"><RedirectWithQuery to="/groupes-priere" /></Route>
      <Route path="/visites-malades"><RedirectWithQuery to="/visites-malades" /></Route>
      <Route path="/aides-sociales"><RedirectWithQuery to="/aides-sociales" /></Route>
      <Route path="/kermesse"><RedirectWithQuery to="/kermesse" /></Route>
      <Route path="/notifications"><RedirectWithQuery to="/notifications" /></Route>
      <Route path="/portail-fidele"><RedirectWithQuery to="/portail-fidele" /></Route>
      <Route path="/rapport-diocese"><RedirectWithQuery to="/rapport-diocese" /></Route>
      <Route path="/page/:id"><ProtectedRoute component={PagePlaceholder} /></Route>
      <Route component={NotFound} />
    </Switch>
    </React.Suspense>
  );
}

function AppShell() {
  const { user } = useAuth();
  const [location] = useLocation();
  const isAuthPage = location === "/" || location === "/concept";
  // Chaque écran s'ouvre en haut ; un retour arrière retrouve sa position.
  const zoneContenu = React.useRef<HTMLElement>(null);
  useDefilementEcran(zoneContenu);
  return (
    <div className="flex h-screen overflow-hidden bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:bg-brand-primary focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-accent"
      >
        Aller au contenu principal
      </a>
      {user && !isAuthPage && <AppSidebar />}
      {user && !isAuthPage && <MobileNavTrigger />}
      {user && !isAuthPage && <AppShellHeader />}
      {/* Lien « Aide » sous le titre des écrans de l'argent (77, 91, 9, 81,
          90), vers leur question d'Assistance (20/10/2026). */}
      {user && !isAuthPage && <AideEnTete />}
      {/* Conformité DA : UN SEUL header par page — le bandeau bleu (PageHeader /
          DashboardHeader). L'ancien <AutoBreadcrumb /> global empilait une
          deuxième barre de navigation au-dessus de ce bandeau : supprimé. */}
      <main id="main" ref={zoneContenu} className="flex-1 overflow-auto min-w-0">
        {user && !isAuthPage && <ParishStatusBanner />}
        <Router />
      </main>
      {import.meta.env.DEV && user && !isAuthPage && <FloatingPagesMenu />}
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <TooltipProvider>
          <AuthProvider>
            <ParishProvider>
              <Toaster />
              <AppShell />
            </ParishProvider>
          </AuthProvider>
        </TooltipProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}

export default App;
